import { useMutation } from "@apollo/client";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { gql } from "generated-graphql";
import { CohortMemberStatus } from "generated-graphql/graphql";

const SPLIT_COHORT_MUTATION = gql(`
  mutation SplitCohort($cohortId: ID!) {
    splitCohort(cohortId: $cohortId) {
      closedCohort {
        id
        status
        reportKind
      }
      newCohort {
        id
        status
        reportKind
      }
    }
  }
`);

interface SplitCohortDialogProps {
  open: boolean;
  onClose: () => void;
  cohortId: string;
  members: Array<{
    id: string;
    status: CohortMemberStatus;
  }>;
  onSuccess?: () => void;
}

export function SplitCohortDialog({
  open,
  onClose,
  cohortId,
  members,
  onSuccess,
}: SplitCohortDialogProps) {
  const alerts = useAlerts();

  const [splitCohort, { loading }] = useMutation(SPLIT_COHORT_MUTATION, {
    onCompleted: () => {
      alerts.success("Cohort split successfully");
      onClose();
      onSuccess?.();
    },
    onError: (error) => {
      alerts.error(`Failed to split cohort: ${error.message}`);
    },
  });

  const readyMembers = members.filter((member) => member.status === CohortMemberStatus.Ready);
  const notReadyMembers = members.filter((member) => member.status !== CohortMemberStatus.Ready);

  const handleConfirm = () => {
    splitCohort({
      variables: { cohortId },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6">⚠️ Unblock Cohort ⚠️</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Typography variant="body1" paragraph>
            This will split the cohort and close the present cohort with{" "}
            <strong>{readyMembers.length}</strong> members that are in a ready state. The{" "}
            <strong>{notReadyMembers.length}</strong> members that are not in a ready state will be
            placed in a new open cohort.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. The current cohort will be closed and a new cohort will be
            created for the remaining members.
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} variant="contained" color="warning" disabled={loading}>
          Yes, Split Cohort
        </Button>
      </DialogActions>
    </Dialog>
  );
}
